import { CachedImageData, AtlasMapValue } from 'game-ui-types'
import {
    getAtlasImageName,
    getAtlasImageUrl,
    getAtlasJsonUrl,
    getFallbackAtlasImageName,
    isObjectUrl,
    TexturePackerAtlas,
    toAtlasMapValues,
} from '@utils/atlas'

type AtlasPathInput = string | string[]

export interface AtlasSlice {
    // atlasMap: { [atlasName]: { [imageName]: AtlasMapValue } }
    atlasMap: Record<string, Record<string, AtlasMapValue>>
    cachedImages: Record<string, CachedImageData>

    preloadImages: (atlasUrls: string[]) => void
    addAtlasMap: (newAtlasMap: Record<string, AtlasMapValue[]>) => void
    loadAtlasesFromPath: (atlasPathInput: AtlasPathInput, atlasJsonPaths?: string[]) => Promise<void>

    findElementByImageName: (name: string) => { atlasName: string; value: AtlasMapValue } | undefined
}

export const createAtlasSlice = (
    set: (fn: (state: AtlasSlice) => Partial<AtlasSlice>) => void,
    get: () => AtlasSlice
): AtlasSlice => {
    const loadedAtlasJsonUrls = new Set<string>()
    const loadingAtlasJsonUrls = new Set<string>()
    const loadingAtlasImages = new Set<string>()
    const objectUrls = new Set<string>()

    return {
        atlasMap: {},
        cachedImages: {},
        addAtlasMap: (newAtlasMap) =>
            set((state) => {
                // Change from { [atlasName]: AtlasMapValue[] } to { [atlasName]: { [imageName]: AtlasMapValue } }
                const formattedAtlas = Object.entries(newAtlasMap).reduce(
                    (newObj, [atlasName, textureArray]) => {
                        newObj[atlasName] = textureArray.reduce(
                            (newTextureObj, entry) => {
                                newTextureObj[entry.name] = entry

                                return newTextureObj
                            },
                            {} as Record<string, AtlasMapValue>
                        )

                        return newObj
                    },
                    {} as Record<string, Record<string, AtlasMapValue>>
                )

                return { atlasMap: { ...state.atlasMap, ...formattedAtlas } }
            }),

        preloadImages: async (atlasUrls: string[]) => {
            const { cachedImages } = get()
            const imagesToLoad = atlasUrls.filter((url) => !cachedImages[url])

            if (imagesToLoad.length === 0) return

            const imageCache: Record<string, CachedImageData> = {}

            await Promise.all(
                imagesToLoad.map(
                    (url) =>
                        new Promise<void>((resolve) => {
                            const img = new Image()

                            img.crossOrigin = 'Anonymous'
                            img.src = url
                            img.onload = () => {
                                const canvas = document.createElement('canvas')

                                canvas.width = img.width
                                canvas.height = img.height

                                const ctx = canvas.getContext('2d')

                                ctx?.drawImage(img, 0, 0)

                                imageCache[url] = {
                                    url: canvas.toDataURL('image/png'),
                                    width: img.width,
                                    height: img.height,
                                }

                                resolve()
                            }
                            img.onerror = () => resolve()
                        })
                )
            )

            set((state: AtlasSlice) => ({
                cachedImages: { ...state.cachedImages, ...imageCache },
            }))
        },

        loadAtlasesFromPath: async (atlasPathInput: AtlasPathInput, atlasJsonPaths = []) => {
            const loadedAtlasMap: Record<string, AtlasMapValue[]> = {}
            const imageCache: Record<string, CachedImageData> = {}
            const { atlasMap, cachedImages } = get()
            const atlasJsonUrls =
                typeof atlasPathInput === 'string' && atlasJsonPaths.length > 0
                    ? atlasJsonPaths
                          .map((atlasJsonPath) => getAtlasJsonUrl(atlasJsonPath, atlasPathInput))
                          .filter((url): url is string => Boolean(url))
                    : [atlasPathInput]
                          .flat()
                          .map((atlasPath) => getAtlasJsonUrl(atlasPath))
                          .filter((url): url is string => Boolean(url))

            await Promise.all(
                atlasJsonUrls.map(async (atlasJsonUrl) => {
                    let atlasImageUrl = ''

                    try {
                        if (loadedAtlasJsonUrls.has(atlasJsonUrl) || loadingAtlasJsonUrls.has(atlasJsonUrl)) {
                            return
                        }

                        loadingAtlasJsonUrls.add(atlasJsonUrl)

                        const fallbackImageName = getFallbackAtlasImageName(atlasJsonUrl)
                        const fallbackAtlasImageUrl = getAtlasImageUrl(fallbackImageName, atlasJsonUrl)

                        if (
                            atlasMap[fallbackAtlasImageUrl] ||
                            cachedImages[fallbackAtlasImageUrl] ||
                            loadingAtlasImages.has(fallbackAtlasImageUrl)
                        ) {
                            return
                        }

                        const response = await fetch(atlasJsonUrl)

                        if (!response.ok) {
                            console.warn(`Failed to load atlas json: ${atlasJsonUrl}`)

                            return
                        }

                        const atlas = (await response.json()) as TexturePackerAtlas

                        if (!atlas.frames) {
                            console.warn(`Atlas json has no frames: ${atlasJsonUrl}`)

                            return
                        }

                        atlasImageUrl = getAtlasImageUrl(getAtlasImageName(atlas, atlasJsonUrl), atlasJsonUrl)

                        if (
                            atlasMap[atlasImageUrl] ||
                            cachedImages[atlasImageUrl] ||
                            loadingAtlasImages.has(atlasImageUrl)
                        ) {
                            loadedAtlasJsonUrls.add(atlasJsonUrl)

                            return
                        }

                        loadingAtlasImages.add(atlasImageUrl)

                        if (atlas.meta?.size) {
                            try {
                                const imageResponse = await fetch(atlasImageUrl)
                                const imageUrl = imageResponse.ok
                                    ? URL.createObjectURL(await imageResponse.blob())
                                    : atlasImageUrl

                                if (!imageResponse.ok) {
                                    console.warn(`Failed to load atlas image: ${atlasImageUrl}`)
                                }

                                if (isObjectUrl(imageUrl)) {
                                    objectUrls.add(imageUrl)
                                }

                                imageCache[atlasImageUrl] = {
                                    url: imageUrl,
                                    width: atlas.meta.size.w,
                                    height: atlas.meta.size.h,
                                }
                            } catch {
                                imageCache[atlasImageUrl] = {
                                    url: atlasImageUrl,
                                    width: atlas.meta.size.w,
                                    height: atlas.meta.size.h,
                                }
                            }
                        }

                        loadingAtlasImages.delete(atlasImageUrl)

                        loadedAtlasMap[atlasImageUrl] = toAtlasMapValues(atlas)
                        loadedAtlasJsonUrls.add(atlasJsonUrl)
                    } catch (error) {
                        console.warn(`Failed to load atlas: ${atlasJsonUrl}`, error)

                        return
                    } finally {
                        loadingAtlasJsonUrls.delete(atlasJsonUrl)

                        if (atlasImageUrl) {
                            loadingAtlasImages.delete(atlasImageUrl)
                        }
                    }
                })
            )

            if (Object.keys(loadedAtlasMap).length === 0) {
                return
            }

            if (Object.keys(imageCache).length > 0) {
                set((state: AtlasSlice) => ({
                    cachedImages: {
                        ...state.cachedImages,
                        ...Object.entries(imageCache).reduce(
                            (cache, [atlasImageUrl, cachedImageData]) => {
                                const previousUrl = state.cachedImages[atlasImageUrl]?.url

                                if (previousUrl && previousUrl !== cachedImageData.url && objectUrls.has(previousUrl)) {
                                    URL.revokeObjectURL(previousUrl)
                                    objectUrls.delete(previousUrl)
                                }

                                cache[atlasImageUrl] = cachedImageData

                                return cache
                            },
                            {} as Record<string, CachedImageData>
                        ),
                    },
                }))
            }

            get().addAtlasMap(loadedAtlasMap)

            const imagesWithoutSize = Object.keys(loadedAtlasMap).filter((url) => !imageCache[url])

            if (imagesWithoutSize.length > 0) {
                await get().preloadImages(imagesWithoutSize)
            }
        },

        findElementByImageName: (name) => {
            let found: { atlasName: string; value: AtlasMapValue } | undefined

            for (const [atlasName, atlasTextures] of Object.entries(get().atlasMap)) {
                if (atlasTextures[name]) found = { atlasName, value: atlasTextures[name] }

                if (found) break
            }

            return found
        },
    }
}
